import { z } from 'zod'
import {
  CouponType,
  PrerequisiteType,
  PurchaseConditionType,
  PurchaseConditionAmountType
} from 'src/lib/types/discountsV2'
import {
  CustomerEligibilitySchema,
  StackabilitySchema,
  PrerequisiteCatalogueSchema,
  nonNegativeNumber,
  refineCatalogueDuplicates
} from './common'

const BasePrerequisiteFields = {
  customerEligibility: CustomerEligibilitySchema,
  usageLimit: z.object({
    maxApplicableCount: z.number().optional().nullable(),
    maxApplicablePerUserCount: z.number().optional().nullable()
  }),
  stackability: StackabilitySchema,
  availability: z.object({
    active: z.boolean().optional(),
    enabledChannelIds: z.array(z.string()).min(1, 'Please select at least 1 channel').optional(),
    startDate: z.number().nullable(),
    endDate: z.number().nullable().optional(),
    discoverable: z.boolean().optional()
  })
}

export const PricePrerequisiteSchema = z
  .object({
    type: z.enum([CouponType.AMOUNT, CouponType.BXGY, CouponType.SHIPPING]),
    ...BasePrerequisiteFields,
    prerequisiteType: z.literal(PrerequisiteType.PRICE),
    purchaseConditionType: z.literal(PurchaseConditionType.CART_AMOUNT),
    purchaseConditionAmountType: z.enum([
      PurchaseConditionAmountType.ENTIRE_CART,
      PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
      PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
    ]),
    prerequisiteSubtotalRange: nonNegativeNumber.optional().nullable(),
    prerequisiteMaxOrderValue: nonNegativeNumber.optional().nullable(),
    prerequisiteCatalogue: PrerequisiteCatalogueSchema,
    metaData: z.object({
      prerequisiteCatalogue: z.array(z.any())
    })
  })
  .superRefine((data, ctx) => {
    if (data.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      if (data.purchaseConditionType === PurchaseConditionType.CART_AMOUNT) {
        if (!data.prerequisiteSubtotalRange) {
          ctx.addIssue({
            code: 'custom',
            message: 'Enter min limit',
            path: ['prerequisiteSubtotalRange']
          })
        }
        if (data.prerequisiteSubtotalRange || data.prerequisiteMaxOrderValue) {
          if (
            data.prerequisiteSubtotalRange != null &&
            data.prerequisiteMaxOrderValue != null &&
            data.prerequisiteSubtotalRange >= data.prerequisiteMaxOrderValue
          ) {
            ctx.addIssue({
              code: 'custom',
              message: 'Max limit cannot be less than or equal to min limit',
              path: ['prerequisiteSubtotalRange']
            })
          }
        }
      }
    }
    if (data.purchaseConditionAmountType !== PurchaseConditionAmountType.ENTIRE_CART) {
      refineCatalogueDuplicates(data.prerequisiteCatalogue, ctx, 'prerequisiteCatalogue')
      if (!data.prerequisiteCatalogue || data.prerequisiteCatalogue.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select at least one product',
          path: ['prerequisiteCatalogue']
        })
      } else {
        data.prerequisiteCatalogue.forEach((item, index) => {
          const isAllProducts =
            data.metaData?.prerequisiteCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
            data.metaData?.prerequisiteCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

          if ((!item.entities || item.entities.length === 0) && !isAllProducts) {
            ctx.addIssue({
              code: 'custom',
              message: 'Select at least one product',
              path: ['prerequisiteCatalogue', index, 'entities']
            })
          }
          if (item.type === PrerequisiteType.QUANTITY && !item.quantity) {
            ctx.addIssue({
              code: 'custom',
              message: 'Quantity must be greater than 1',
              path: ['prerequisiteCatalogue', index, 'quantity']
            })
          }
          if (item.type === PrerequisiteType.PRICE && !item.price) {
            ctx.addIssue({
              code: 'custom',
              message: 'Price must be greater than 0',
              path: ['prerequisiteCatalogue', index, 'price']
            })
          }
          if (item.type === PrerequisiteType.PRICE && item.price && item.maxPrice && item.maxPrice <= item.price) {
            ctx.addIssue({
              code: 'custom',
              message: 'Max Amount must be greater than Min Amount',
              path: ['prerequisiteCatalogue', index, 'maxPrice']
            })
          }
        })
      }
    }
  })

export const QuantityPrerequisiteSchema = z
  .object({
    type: z.enum([CouponType.AMOUNT, CouponType.BXGY, CouponType.SHIPPING]),
    ...BasePrerequisiteFields,
    prerequisiteType: z.literal(PrerequisiteType.QUANTITY),
    purchaseConditionType: z.literal(PurchaseConditionType.CART_QUANTITY),
    purchaseConditionAmountType: z.enum([
      PurchaseConditionAmountType.ENTIRE_CART,
      PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
      PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
    ]),
    prerequisiteCountToEntitlement: nonNegativeNumber.optional().nullable(),
    prerequisiteMaxCountForEntitlement: nonNegativeNumber.optional().nullable(),
    prerequisiteCatalogue: PrerequisiteCatalogueSchema,
    metaData: z.object({
      prerequisiteCatalogue: z.array(z.any())
    })
  })
  .superRefine((data, ctx) => {
    if (data.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      if (!data.prerequisiteCountToEntitlement) {
        ctx.addIssue({
          code: 'custom',
          message: 'Enter min limit',
          path: ['prerequisiteCountToEntitlement']
        })
      }
      if (data.purchaseConditionType === PurchaseConditionType.CART_QUANTITY) {
        if (
          data.prerequisiteMaxCountForEntitlement != null &&
          data.prerequisiteCountToEntitlement != null &&
          data.prerequisiteMaxCountForEntitlement < data.prerequisiteCountToEntitlement
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'Max limit cannot be less than min limit',
            path: ['prerequisiteCountToEntitlement']
          })
        }
      }
    }
    if (data.purchaseConditionAmountType !== PurchaseConditionAmountType.ENTIRE_CART) {
      refineCatalogueDuplicates(data.prerequisiteCatalogue, ctx, 'prerequisiteCatalogue')
      if (!data.prerequisiteCatalogue || data.prerequisiteCatalogue.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select at least one product',
          path: ['prerequisiteCatalogue']
        })
      } else {
        data.prerequisiteCatalogue.forEach((item, index) => {
          const isAllProducts =
            data.metaData?.prerequisiteCatalogue?.[index]?.[0]?.id === 'shopflo-all-products' ||
            data.metaData?.prerequisiteCatalogue?.[index]?.[0]?.id === 'shopflo-all-collections'

          if ((!item.entities || item.entities.length === 0) && !isAllProducts) {
            ctx.addIssue({
              code: 'custom',
              message: 'Select at least one product',
              path: ['prerequisiteCatalogue', index, 'entities']
            })
          }
          if (item.type === PrerequisiteType.QUANTITY && !item.quantity) {
            ctx.addIssue({
              code: 'custom',
              message: 'Quantity must be greater than 1',
              path: ['prerequisiteCatalogue', index, 'quantity']
            })
          }
          if (item.type === PrerequisiteType.PRICE && !item.price) {
            ctx.addIssue({
              code: 'custom',
              message: 'Price must be greater than 0',
              path: ['prerequisiteCatalogue', index, 'price']
            })
          }
          if (item.type === PrerequisiteType.PRICE && !item.price) {
            ctx.addIssue({
              code: 'custom',
              message: 'Price must be greater than 0',
              path: ['prerequisiteCatalogue', index, 'price']
            })
          }
          if (
            item.type === PrerequisiteType.QUANTITY &&
            item.quantity &&
            item.maxQuantity &&
            item.maxQuantity < item.quantity
          ) {
            ctx.addIssue({
              code: 'custom',
              message: 'Max Quantity must be greater than Min Quantity',
              path: ['prerequisiteCatalogue', index, 'maxQuantity']
            })
          }
        })
      }
    }
  })

export const NonePrerequisiteSchema = z
  .object({
    type: z.enum([CouponType.AMOUNT, CouponType.BXGY, CouponType.SHIPPING]),
    ...BasePrerequisiteFields,
    prerequisiteType: z.literal(PrerequisiteType.NONE),
    purchaseConditionType: z.enum([
      PurchaseConditionType.NONE,
      PurchaseConditionType.CART_AMOUNT,
      PurchaseConditionType.CART_QUANTITY
    ]),
    purchaseConditionAmountType: z.enum([
      PurchaseConditionAmountType.ENTIRE_CART,
      PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
      PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
    ]),
    prerequisiteCatalogue: PrerequisiteCatalogueSchema,
    metaData: z.object({
      prerequisiteCatalogue: z.array(z.any())
    })
  })
  .superRefine((data, ctx) => {
    refineCatalogueDuplicates(data.prerequisiteCatalogue, ctx, 'prerequisiteCatalogue')
  })

export const BundlePrerequisiteSchema = z.object({
  type: z.literal(CouponType.BUNDLE),
  ...BasePrerequisiteFields,
  prerequisiteType: z.literal(PrerequisiteType.NONE).optional()
})

export const PrerequisiteSchema = z.union([
  PricePrerequisiteSchema,
  QuantityPrerequisiteSchema,
  NonePrerequisiteSchema,
  BundlePrerequisiteSchema
])
